import { useState, useEffect, useCallback } from 'react';
import { toaster } from '@/components/ui/toaster';
import { baseApi } from '@/utils/api';
import { Content } from '@/lib/types';

interface Props {
  initialized: boolean;
  referenceId?: string;
  selectListFilters: { key: string; val: string }[];
  facetFields: string[];
  searchQuery: string;
  pageNumber: number;
  filters?: any[];
  sortKey?: string;
  hasHeader?: boolean;
}

const useContentData = ({
  initialized,
  referenceId,
  selectListFilters,
  facetFields,
  searchQuery,
  pageNumber,
  filters,
  sortKey = 'recent',
  hasHeader = false,
}: Props) => {
  const [contents, setContents] = useState<Content[]>([]);
  const [optionConfig, setOptionConfig] = useState<{
    [key: string]: {
      [key: string]: number;
    };
  }>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [reload, setReload] = useState(false);
  const [header, setHeader] = useState<{ name: string; description: string } | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const fetchData = useCallback(async () => {
    if (initialized === false || !referenceId) {
      setContents([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      let sort: string[] = [];
      if (sortKey === 'recent') {
        sort = ['created_at:desc'];
      } else if (sortKey === 'titleAsc') {
        sort = ['name:asc'];
      } else if (sortKey === 'titleDesc') {
        sort = ['name:desc'];
      }

      const response = await baseApi.post(`/public/contents/search`, {
        content_type_reference_id: referenceId,
        q: searchQuery,
        page: pageNumber,
        page_size: 12,
        sort: sort,
        filters: filters,
        select_list_filters: selectListFilters,
        facet_fields: facetFields,
      });

      const stored = localStorage.getItem('deletedContentIds');
      const deletedContentIds: string[] = stored ? JSON.parse(stored) : [];

      const hits = response.data.data.hits.filter(
        (content: { id: string }) => !deletedContentIds.includes(content.id),
      );

      if (hasHeader && response.data.data.ContentType && response.data.data.Workspace) {
        setHeader({
          name: response.data.data.ContentType.name,
          description: response.data.data.Workspace.name,
        });
      }

      // Infinite scroll appends pages; never let one content id land twice
      // (page offsets shift when rows are created mid-scroll, and the API's
      // hybrid merge can prepend a row on page 1).
      setContents((prevContents) => {
        const merged: Content[] = pageNumber === 1 ? [] : [...prevContents];
        const seen = new Set(merged.map((content) => String(content.id)));
        for (const hit of hits as Content[]) {
          const key = String(hit.id);
          if (seen.has(key)) continue;
          seen.add(key);
          merged.push(hit);
        }
        return merged;
      });
      setOptionConfig(response.data.data.facet_distribution || {});
      setTotalPages(response.data.data.total_pages);
      setHasMore(response.data.data.total_pages > response.data.data.current_page);
      setIsLoading(false);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to fetch contents',
      });
      setIsLoading(false);
    }
  }, [
    initialized,
    hasHeader,
    referenceId,
    filters,
    selectListFilters,
    facetFields,
    pageNumber,
    sortKey,
    searchQuery,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData, reload]);

  const reloadContents = () => {
    setReload((prev) => !prev);
  };

  return {
    header,
    contents,
    optionConfig,
    isLoading,
    hasMore,
    totalPages,
    reloadContents,
  };
};

export default useContentData;
